// src/components/Navbar.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTierBadge, getSalesRole } from '../theme/tier';
import { colors, radii } from '../theme/tokens';
import { useTheme } from '../theme/ThemeContext';
import Icon from './ui/Icon';
import BrandMark from './ui/BrandMark';
import { subNavPill } from './ui/SubNav';

const Navbar = () => {
  const { user, logout, isAuthenticated, canAddData, canApprove, isSalesTeam, canAccessNfc } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const [burgerOpen, setBurgerOpen] = useState(false);
  const burgerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (burgerRef.current && !burgerRef.current.contains(e.target)) {
        setBurgerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated) return null;

  const badge = getTierBadge(user?.tier);
  const role = getSalesRole(user?.salesRole);

  const showNfcInBurger = canAccessNfc && !isSalesTeam;
  const hasBurgerItems = canAddData || canApprove || showNfcInBurger;

  const isActive = (to) => location.pathname === to;
  const linkStyle = (to) => ({
    ...styles.link,
    background: isActive(to) ? 'rgba(47,182,92,0.18)' : 'transparent',
    color: isActive(to) ? colors.parGreenBright : colors.text
  });

  // Sales team: the car-parts menu is a sub-menu under the BubbleNav, so it uses
  // the shared transparent pill style (no PAR logo, no account chrome — that
  // lives in the profile bubble now).
  if (isSalesTeam) {
    return (
      <nav style={{ background: 'transparent', padding: '12px 0', position: 'relative', zIndex: 900 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 16px' }}>
          <Link to="/car-parts/home" className="subnav-item" style={subNavPill(isActive('/car-parts/home'))}>
            <Icon name="home" size={18} /> <span className="nav-link-text">Home</span>
          </Link>
          <Link to="/car-parts/find" className="subnav-item" style={subNavPill(isActive('/car-parts/find'))}>
            <Icon name="search" size={18} /> <span className="nav-link-text">Find</span>
          </Link>
          {canAddData && (
            <Link to="/car-parts/add" className="subnav-item" style={subNavPill(isActive('/car-parts/add'))}>
              <Icon name="add_circle" size={18} /> <span className="nav-link-text">Add</span>
            </Link>
          )}
          {hasBurgerItems && (
            <div style={styles.burgerWrapper} ref={burgerRef}>
              <button
                className="subnav-item"
                style={{ ...subNavPill(false), padding: '8px 12px' }}
                onClick={() => setBurgerOpen(!burgerOpen)}
                aria-label="More menu"
              >
                <Icon name={burgerOpen ? 'close' : 'menu'} size={20} />
              </button>
              {burgerOpen && (
                <div style={styles.dropdown}>
                  {canAddData && (
                    <DropLink to="/car-parts/fulfill" icon="assignment" onClick={() => setBurgerOpen(false)}>Fulfill Requests</DropLink>
                  )}
                  {canApprove && (
                    <>
                      <DropLink to="/car-parts/manage-cars" icon="directions_car" onClick={() => setBurgerOpen(false)}>Manage Car List</DropLink>
                      <DropLink to="/car-parts/import" icon="upload_file" onClick={() => setBurgerOpen(false)}>Import CSV</DropLink>
                      <DropLink to="/car-parts/view-all" icon="database" onClick={() => setBurgerOpen(false)}>View All Data</DropLink>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav style={styles.nav}>
      <div style={styles.container}>
        <Link to="/car-parts/home" style={styles.logo}>
          <BrandMark size={30} withWordmark={!isSalesTeam} />
        </Link>

        <div style={styles.menu}>
          <Link to="/car-parts/home" style={linkStyle('/car-parts/home')}>
            <Icon name="home" size={18} /> <span className="nav-link-text">Home</span>
          </Link>
          <Link to="/car-parts/find" style={linkStyle('/car-parts/find')}>
            <Icon name="search" size={18} /> <span className="nav-link-text">Find</span>
          </Link>
          {canAddData && (
            <Link to="/car-parts/add" style={linkStyle('/car-parts/add')}>
              <Icon name="add_circle" size={18} /> <span className="nav-link-text">Add</span>
            </Link>
          )}
        </div>

        <div style={styles.rightSection} className="nav-right">
          {hasBurgerItems && (
            <div style={styles.burgerWrapper} ref={burgerRef}>
              <button
                style={styles.burgerBtn}
                onClick={() => setBurgerOpen(!burgerOpen)}
                aria-label="More menu"
              >
                <Icon name={burgerOpen ? 'close' : 'menu'} size={20} />
              </button>
              {burgerOpen && (
                <div style={styles.dropdown}>
                  {canAddData && (
                    <DropLink to="/car-parts/fulfill" icon="assignment" onClick={() => setBurgerOpen(false)}>Fulfill Requests</DropLink>
                  )}
                  {showNfcInBurger && (
                    <DropLink to="/nfc-card" icon="contact_mail" onClick={() => setBurgerOpen(false)}>NFC Business Card</DropLink>
                  )}
                  {canApprove && (
                    <>
                      {!isSalesTeam && (
                        <DropLink to="/admin" icon="settings" onClick={() => setBurgerOpen(false)}>Admin Dashboard</DropLink>
                      )}
                      <DropLink to="/car-parts/manage-cars" icon="directions_car" onClick={() => setBurgerOpen(false)}>Manage Car List</DropLink>
                      <DropLink to="/car-parts/import" icon="upload_file" onClick={() => setBurgerOpen(false)}>Import CSV</DropLink>
                      <DropLink to="/car-parts/view-all" icon="database" onClick={() => setBurgerOpen(false)}>View All Data</DropLink>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {!isSalesTeam && (
            <div style={styles.userSection}>
              <div style={styles.userInfo} className="nav-user-info">
                {user?.picture && (
                  <img src={user.picture} alt={user.name} style={styles.avatar} />
                )}
                <div style={styles.userDetails}>
                  <div style={styles.userName} className="nav-username">{user?.name}</div>
                  <div style={styles.badgeContainer}>
                    <span style={{ ...styles.tierBadge, background: badge.color }}>
                      <Icon name={badge.icon} size={11} color="#1a1a1a" /> {badge.label}
                    </span>
                    {role && (
                      <span style={{ ...styles.salesBadge, background: role.color }}>
                        <Icon name={role.icon} size={11} color="#fff" /> {role.label}
                      </span>
                    )}
                  </div>
                </div>
                <div style={styles.tokens} className="nav-hide-mobile">
                  <Icon name="paid" size={14} color={colors.parGreenBright} /> {user?.tokens || 0}
                </div>
              </div>
              <button
                onClick={toggleTheme}
                style={styles.iconToggleBtn}
                title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                aria-label="Toggle light/dark mode"
              >
                <Icon name={mode === 'light' ? 'light_mode' : 'dark_mode'} size={16} />
              </button>
              <button onClick={logout} style={styles.logoutBtn} title="Logout">
                <Icon name="logout" size={16} />
                <span className="nav-hide-mobile">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

const DropLink = ({ to, icon, children, onClick }) => (
  <Link to={to} style={styles.dropdownLink} onClick={onClick}>
    <Icon name={icon} size={18} color={colors.parGreenBright} />
    {children}
  </Link>
);

const styles = {
  nav: {
    background: colors.navBg,
    borderBottom: `1px solid ${colors.glassBorder}`,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    padding: '8px 0',
    position: 'sticky',
    top: 0,
    zIndex: 1000
  },
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  logo: { textDecoration: 'none', flexShrink: 0, display: 'flex' },
  menu: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  link: { display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', padding: '7px 14px', borderRadius: radii.pill, transition: 'background 0.2s, color 0.2s', fontWeight: 600, fontSize: 14 },
  rightSection: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  burgerWrapper: { position: 'relative' },
  burgerBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: colors.glass, color: colors.text, border: `1px solid ${colors.glassBorder}`, padding: '7px 12px', borderRadius: radii.pill, cursor: 'pointer', lineHeight: 1 },
  dropdown: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: colors.menuBg, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md, boxShadow: '0 18px 48px rgba(0,0,0,0.28)', minWidth: 210, overflow: 'hidden', zIndex: 9999, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' },
  dropdownLink: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', color: colors.text, textDecoration: 'none', fontSize: 14, fontWeight: 500, borderBottom: `1px solid ${colors.glassBorder}` },
  userSection: { display: 'flex', alignItems: 'center', gap: 10 },
  userInfo: { display: 'flex', alignItems: 'center', gap: 8, background: colors.glass, border: `1px solid ${colors.glassBorder}`, padding: '5px 12px', borderRadius: radii.pill },
  avatar: { width: 30, height: 30, borderRadius: '50%', border: `2px solid ${colors.glassBorderStrong}` },
  userDetails: { display: 'flex', flexDirection: 'column', gap: 3 },
  userName: { color: colors.text, fontSize: 13, fontWeight: 700 },
  badgeContainer: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  tierBadge: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '2px 7px', borderRadius: 999, color: '#1a1a1a', fontWeight: 800 },
  salesBadge: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '2px 7px', borderRadius: 999, color: 'white', fontWeight: 800 },
  tokens: { display: 'inline-flex', alignItems: 'center', gap: 4, color: colors.text, fontSize: 12, fontWeight: 700, background: 'rgba(47,182,92,0.10)', padding: '4px 10px', borderRadius: 999 },
  logoutBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: colors.glass, color: colors.text, border: `1px solid ${colors.glassBorder}`, padding: '7px 14px', borderRadius: radii.pill, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  iconToggleBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: colors.glass, color: colors.text, border: `1px solid ${colors.glassBorder}`, padding: '7px', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', flexShrink: 0 }
};

export default Navbar;
